import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FilterX, Search, CalendarIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { getSelectSellers } from "@/Api/sellers";
import { selectUsers } from "@/Api/user";
import { useAuth } from "@/hooks/auth";
import { TypeUser } from "@/types/userType";
import { WalletOperationsFilters } from "../shared/types";

interface Seller {
  id: number;
  uuid: string;
  user: {
    id: number;
    name: string;
  };
}

interface User {
  id: number;
  uuid: string;
  name: string;
}

interface FiltersProps {
  onFilter: (filters: WalletOperationsFilters) => void;
  loading?: boolean;
}

export function WalletOperationsFiltersComponent({ onFilter, loading }: FiltersProps) {
  const { user } = useAuth();
  const isUnityResponsible = user?.user_type_id === TypeUser.unity_responsible;

  const [sellers, setSellers] = useState<Seller[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [sellerId, setSellerId] = useState<string>("all");
  const [type, setType] = useState<string>("all");
  const [createdById, setCreatedById] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  useEffect(() => {
    loadSellers();
    loadUsers();
  }, []);

  const loadSellers = async () => {
    setLoadingSellers(true);
    try {
      const params: { unity_id?: string; is_active?: boolean } = { is_active: true };
      if (isUnityResponsible && user?.unity_id) {
        params.unity_id = user.unity_id.toString();
      }
      const { data } = await getSelectSellers(params);
      if (data?.response) {
        setSellers(data.response);
      }
    } catch (error) {
      console.error("Error loading sellers:", error);
    } finally {
      setLoadingSellers(false);
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data } = await selectUsers();
      if (data?.response) {
        setUsers(data.response);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleFilter = () => {
    const filters: WalletOperationsFilters = {};

    if (sellerId && sellerId !== "all") {
      filters.seller_id = parseInt(sellerId);
    }
    if (type && type !== "all") {
      filters.type = type as "addition" | "subtraction";
    }
    if (createdById && createdById !== "all") {
      filters.created_by_id = parseInt(createdById);
    }
    if (dateFrom) {
      filters.date_from = format(dateFrom, "yyyy-MM-dd");
    }
    if (dateTo) {
      filters.date_to = format(dateTo, "yyyy-MM-dd");
    }

    onFilter(filters);
  };

  const handleClearFilters = () => {
    setSellerId("all");
    setType("all");
    setCreatedById("all");
    setDateFrom(undefined);
    setDateTo(undefined);
    onFilter({});
  };

  return (
    <div className="space-y-4 p-4 bg-zinc-50 dark:bg-[#2c2c2c] rounded-lg">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="space-y-2">
          <Label>Vendedor</Label>
          <Select value={sellerId} onValueChange={setSellerId} disabled={loadingSellers}>
            <SelectTrigger>
              <SelectValue placeholder={loadingSellers ? "Carregando..." : "Todos"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {sellers.map((seller) => (
                <SelectItem key={seller.id} value={seller.id.toString()}>
                  {seller.user?.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Tipo</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger>
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="addition">Adição</SelectItem>
              <SelectItem value="subtraction">Subtração</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Gerado por</Label>
          <Select value={createdById} onValueChange={setCreatedById} disabled={loadingUsers}>
            <SelectTrigger>
              <SelectValue placeholder={loadingUsers ? "Carregando..." : "Todos"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id.toString()}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Data inicial</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !dateFrom && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateFrom ? format(dateFrom, "dd/MM/yyyy", { locale: ptBR }) : <span>Selecione</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={dateFrom}
                onSelect={setDateFrom}
                initialFocus
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>Data final</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !dateTo && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateTo ? format(dateTo, "dd/MM/yyyy", { locale: ptBR }) : <span>Selecione</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={dateTo}
                onSelect={setDateTo}
                initialFocus
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleClearFilters} className="h-9" disabled={loading}>
          <FilterX className="h-4 w-4 mr-2" />
          Limpar filtros
        </Button>
        <Button
          onClick={handleFilter}
          className="h-9 bg-blue-600 hover:bg-blue-700"
          disabled={loading}
        >
          <Search className="h-4 w-4 mr-2" />
          Filtrar
        </Button>
      </div>
    </div>
  );
}
